/* -----------------Globals--------------- */
import { Dialog, Text } from "react-native-paper";

import { useClerk } from "@clerk/clerk-expo";
/* -----------------Helpers & Hooks--------------- */
import { useAppTheme } from "@hooks/useAppTheme";

/* -----------------Child components--------------- */

import { events } from "@constants/tracking";
import useAnalytics from "@hooks/useAnalytics";
import useClerkUser from "@hooks/useClerkUser";
import { storage } from "@memorang/helpers/src/storage";
import Button from "@memorang/ui/src/components/Button";
import DialogWrapper from "@memorang/ui/src/components/DialogWrapper";
import { useQueryClient } from "@tanstack/react-query";
/* -----------------Third parties--------------- */
import { trackLogout } from "analytics";
import { useRouter } from "expo-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

type Props = {
	visible: boolean;
	handleClose: () => void;
};
export const SignoutDialog = ({ visible, handleClose }: Props) => {
	const theme = useAppTheme();
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	const { email } = useClerkUser();

	const { explicitCallTrackCustomEvent } = useAnalytics();

	useEffect(() => {
		if (visible) {
			explicitCallTrackCustomEvent({
				eventName: events.dialogOpened,
				type: "SignOut",
			});
		}
	}, [visible, explicitCallTrackCustomEvent]);

	const { signOut } = useClerk();
	const router = useRouter();
	const handleSignout = async () => {
		explicitCallTrackCustomEvent({
			eventName: events.userLoggedOut,
			email,
		});
		queryClient.clear();
		storage.clearAll();
		await signOut();
		trackLogout();
		handleClose();
		router.replace("/");
	};

	const handleDialogClose = () => {
		handleClose();
		explicitCallTrackCustomEvent({
			eventName: events.dialogClosed,
			type: "SignOut",
		});
	};
	return (
		<DialogWrapper
			visible={visible}
			handleClose={handleDialogClose}
			maxWidth={"xs"}
		>
			<Dialog.Title>{t("account.signOut")}</Dialog.Title>
			<Dialog.Content
				style={{
					maxWidth: 400,
				}}
			>
				<Text variant="bodyMedium">{t("account.signOutConfirmation")}</Text>
			</Dialog.Content>
			<Dialog.Actions>
				<Button
					labelStyle={{
						color: theme.colors.onSurfaceVariant,
					}}
					mode="text"
					onPress={handleDialogClose}
				>
					{t("common.cancel")}
				</Button>
				<Button mode="text" onPress={handleSignout}>
					{t("common.ok")}
				</Button>
			</Dialog.Actions>
		</DialogWrapper>
	);
};
